import React, { useState, useEffect } from 'react';
import SystemHealth from './components/SystemHealth';
import EnhancedQueryInterface from './components/EnhancedQueryInterface';
import ProjectsManagement from './components/ProjectsManagement';
import FileManagement from './components/FileManagement';
import enhancedAPI from './services/enhancedApi';
import { 
  AgentHealth, 
  QueryStatus, 
  AgentCommunication, 
  SystemMetrics,
  TaskDistribution as TaskDistributionType 
} from './types';
import './App.css';
import './EnhancedApp.css';

function EnhancedApp() {
  const [activeTab, setActiveTab] = useState<'health' | 'projects' | 'files'>('health');
  const [agents, setAgents] = useState<AgentHealth[]>([]);
  const [activeQuery, setActiveQuery] = useState<QueryStatus | undefined>();
  const [communications, setCommunications] = useState<AgentCommunication[]>([]);
  const [metrics, setMetrics] = useState<SystemMetrics | null>(null);
  const [tokenHistory, setTokenHistory] = useState<any[]>([]);
  const [tasks, setTasks] = useState<TaskDistributionType[]>([]);
  const [selectedQueryId, setSelectedQueryId] = useState<string>('');

  useEffect(() => {
    // Initial data fetch
    fetchSystemHealth();
    fetchSystemMetrics();
    fetchRecentCommunications();

    // Set up polling for metrics
    const metricsInterval = setInterval(() => {
      fetchSystemMetrics();
      fetchTokenHistory();
    }, 30000); // Every 30 seconds

    const healthInterval = setInterval(() => {
      fetchSystemHealth();
    }, 60000); // Every 60 seconds

    return () => {
      clearInterval(metricsInterval);
      clearInterval(healthInterval);
    };
  }, []);

  const fetchSystemHealth = async () => {
    try {
      const health = await enhancedAPI.getSystemHealth();
      setAgents(health);
    } catch (error) {
      console.error('Failed to fetch system health:', error);
    }
  };

  const fetchSystemMetrics = async () => {
    try {
      const metricsData = await enhancedAPI.getSystemMetrics();
      setMetrics(metricsData);
    } catch (error) {
      console.error('Failed to fetch system metrics:', error);
    }
  };

  const fetchRecentCommunications = async () => {
    try {
      const comms = await enhancedAPI.getRecentCommunications();
      setCommunications(comms);
    } catch (error) {
      console.error('Failed to fetch communications:', error);
    }
  };

  const fetchTokenHistory = async () => {
    try {
      const history = await enhancedAPI.getTokenUsage('hour');
      setTokenHistory(history);
    } catch (error) {
      console.error('Failed to fetch token history:', error);
    }
  };

  const handleSubmitQuery = async (query: string, context?: any): Promise<QueryStatus> => {
    const queryStatus = await enhancedAPI.submitQuery(query, context);
    setActiveQuery(queryStatus);
    setSelectedQueryId(queryStatus.queryId);
    
    // Switch to projects tab to see progress
    setActiveTab('projects');
    
    // Use SSE for real-time updates
    const eventSource = enhancedAPI.streamQueryStatus(queryStatus.queryId, (data) => {
      setActiveQuery(data);
      
      if (data.status === 'completed' || data.status === 'failed') {
        eventSource.close();
        
        // Fetch final data
        if (data.status === 'completed') {
          fetchTaskDistribution(queryStatus.queryId);
          enhancedAPI.getCommunicationsByQuery(queryStatus.queryId)
            .then(comms => setCommunications(prev => [...comms, ...prev]))
            .catch(console.error);
        }
      }
    });
    
    return queryStatus;
  };

  const handleRestartAgent = async (agentId: string) => {
    try {
      await enhancedAPI.restartAgent(agentId);
      // Refresh health status
      fetchSystemHealth();
    } catch (error) {
      console.error('Failed to restart agent:', error);
      alert('Failed to restart agent. Check console for details.');
    }
  };

  const fetchTaskDistribution = async (queryId: string) => {
    try {
      const tasksData = await enhancedAPI.getTaskDistribution(queryId);
      setTasks(tasksData);
    } catch (error) {
      console.error('Failed to fetch task distribution:', error);
    }
  };

  return (
    <div className="App enhanced-app">
      <header className="app-header">
        <div className="header-content">
          <h1>Multi-Agent AI System</h1>
          <p className="header-subtitle">Strategic Analysis & Management Dashboard</p>
        </div>
        <div className="connection-status">
          <span className="status-indicator connected" />
          System Operational
        </div>
      </header>

      <nav className="app-nav">
        <button 
          className={`nav-tab ${activeTab === 'health' ? 'active' : ''}`}
          onClick={() => setActiveTab('health')}
        >
          <span className="tab-icon">🏥</span>
          System Health
        </button>
        <button 
          className={`nav-tab ${activeTab === 'projects' ? 'active' : ''}`}
          onClick={() => setActiveTab('projects')}
        >
          <span className="tab-icon">📁</span>
          Projects & Analysis
        </button>
        <button 
          className={`nav-tab ${activeTab === 'files' ? 'active' : ''}`}
          onClick={() => setActiveTab('files')}
        >
          <span className="tab-icon">📄</span>
          File Management
        </button>
      </nav>

      <main className="app-content">
        {activeTab === 'health' && (
          <SystemHealth 
            agents={agents} 
            onRestartAgent={handleRestartAgent}
          />
        )}
        
        {activeTab === 'projects' && (
          <ProjectsManagement />
        )}
        
        {activeTab === 'files' && (
          <FileManagement />
        )}
      </main>
    </div>
  );
}

export default EnhancedApp;